import * as React from 'react'
import styled from 'styled-components'
import Component, { Args } from '@reactions/component'
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom'
import myStorage from 'web-fns/dist/storage'
import { intersection } from 'lodash'
import gql from 'graphql-tag'
import { Query } from 'react-apollo'
import Spin from 'web-makeup/dist/Spin'

import PurchaseOrderRedesigned, {
  OnChangeProps,
} from '../PurchaseOrderRedesigned/PurchaseOrderRedesigned'
import { GetQuoteForm, QuoteInput, VideoProjectTemplateType } from '../../typings/types'
import { isAuthenticated } from '../../helpers/auth'

import { Container, Left, Right, SmallDescription, SmallTitle } from './PurchaseRedesigned.style'

const LoadContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

const Loading = () => (
  <LoadContainer data-testid="loader">
    <Spin size="large" />
  </LoadContainer>
)

export const H5SubText = styled.p`
  color: #666;
  font-size: 0.875rem;
`

export const BoxHeader = styled.header`
  margin-bottom: 1rem;
`

export const GET_QUOTE_FORM = gql`
  query GetQuoteFormV2(
    $quote: QuoteInput!
    $videoProjectTemplateType: VideoProjectTemplateType!
    $trackingUuid: ID
  ) {
    getQuoteForm(
      quote: $quote
      videoProjectTemplateType: $videoProjectTemplateType
      trackingUuid: $trackingUuid
    ) {
      videoProjectTemplateType
      trackingUuid
      quote {
        inspirationVideoUrls
        overview
        shootStartAt
        projectDueAt
        title
        videoProjectTemplate {
          slug
        }
        price {
          amount
          currency {
            code
            name
            symbol
            position
          }
        }
        selectedMasterTimeProfile {
          shootHours
          icon {
            cloudinaryId
            cloudinaryType
          }
          name
          slug
        }
        selectedMasterServiceQualityLevel {
          multiplier
          icon {
            cloudinaryId
            cloudinaryType
          }
          name
          slug
        }
        selectedJobTemplatePackages {
          index
          jobTemplatePackage {
            uuid
            name
            slug
            serviceType
            description
            icon {
              cloudinaryId
              cloudinaryType
            }
          }
          price {
            amount
            currency {
              code
              name
              symbol
              position
            }
          }
          timeProfile {
            shootHours
            name
            slug
          }
          serviceQualityLevel {
            name
            slug
          }
        }
      }
      videoProjectTemplates {
        image {
          cloudinaryId
          cloudinaryType
        }
        slug
        name
        country {
          name
        }
      }
      masterTimeProfilesPriced {
        price {
          amount
          currency {
            code
            name
            symbol
            position
          }
        }
        timeProfile {
          shootHours
          icon {
            cloudinaryId
            cloudinaryType
          }
          name
          slug
        }
      }
      masterServiceQualityLevelsPriced {
        price {
          amount
          currency {
            code
            name
            symbol
            position
          }
        }
        serviceQualityLevel {
          multiplier
          icon {
            cloudinaryId
            cloudinaryType
          }
          name
          slug
        }
      }
      jobTemplatePackages {
        jobTemplatePackage {
          name
          slug
          serviceType
          description
          icon {
            cloudinaryId
            cloudinaryType
          }
        }
        price {
          amount
          currency {
            code
            name
            symbol
            position
          }
        }
        timeProfile {
          shootHours
          name
          slug
        }
        serviceQualityLevel {
          multiplier
          name
          slug
        }
      }
    }
  }
`

const PURCHASE_DATA_STORAGE = 'PURCHASE_DATA_STORAGE'

export const clearLocalState = () => myStorage.clear()

interface PurchaseDataProps {
  videoProjectTemplateType: VideoProjectTemplateType
  quote: GetQuoteForm.Variables['quote']
  trackingUuid: string | null
}

export const formatPurchaseData = (
  quoteInput,
  videoProjectTemplateType,
  trackingUuid
): PurchaseDataProps => ({
  quote: quoteInput,
  videoProjectTemplateType,
  trackingUuid,
})

const getParsedLocalPurchaseDate = (): PurchaseDataProps =>
  JSON.parse(myStorage.getItem(PURCHASE_DATA_STORAGE) || '{}')

export const getLocalPurchaseData = (
  videoProjectTemplateType: PurchaseDataProps['videoProjectTemplateType']
): PurchaseDataProps =>
  getParsedLocalPurchaseDate()[videoProjectTemplateType] ||
  formatPurchaseData({}, videoProjectTemplateType, null)

const setLocalPurchaseData = ({
  quote,
  videoProjectTemplateType,
  trackingUuid,
}: PurchaseDataProps) => {
  const prevState = myStorage.getItem(PURCHASE_DATA_STORAGE)
    ? getParsedLocalPurchaseDate()
    : undefined

  myStorage.setItem(
    PURCHASE_DATA_STORAGE,
    JSON.stringify({
      ...prevState,
      [videoProjectTemplateType]: formatPurchaseData(quote, videoProjectTemplateType, trackingUuid),
    })
  )
}

export const mapQuoteToQuoteInput = (
  quote: GetQuoteForm.GetQuoteForm['quote']
): GetQuoteForm.Variables['quote'] => ({
  overview: quote.overview,
  shootStartAt: quote.shootStartAt,
  projectDueAt: quote.projectDueAt,
  videoProjectTemplateSlug: quote.videoProjectTemplate.slug,
  title: quote.title,
  inspirationVideoUrls: quote.inspirationVideoUrls,
  masterTimeProfileSlug: quote.selectedMasterTimeProfile.slug,
  masterServiceQualityLevelSlug: quote.selectedMasterServiceQualityLevel.slug,
  jobTemplatePackages: quote.selectedJobTemplatePackages.map(job => ({
    jobTemplatePackageSlug: job.jobTemplatePackage.slug,
  })),
})

interface State {
  screen: 'SHOW_FORM' | 'HAS_AUTHENTICATED'
  videoProjectTemplateType?: VideoProjectTemplateType | null
}

export const handleFormSubmit = (
  setState: Args<{}, State>['setState'],
  videoProjectTemplateType?: VideoProjectTemplateType | null
) => () => {
  const templateType = videoProjectTemplateType || VideoProjectTemplateType.ALL
  if (isAuthenticated()) {
    setState({
      screen: 'HAS_AUTHENTICATED',
      videoProjectTemplateType: templateType,
    })
  } else {
    const redirectUrl = encodeURI(
      window.env &&
        `${
          window.env.BRAND_BASE_URL
        }/auth/handoff/brand-selection?videoProjectTemplateType=${templateType}`
    )

    const goToUrl = encodeURI(
      window.env && `${window.env.GEN1_BASE_URL}/brands/signup?redirect_url=${redirectUrl}`
    )

    window.location.assign(goToUrl)
  }
}

export const handleFormChange = (
  refetch,
  videoProjectTemplateType: VideoProjectTemplateType,
  trackingUuid,
  path
) => ({ nextValues, keysOfChangedValues }: OnChangeProps) => {
  // Some form fields don't require a GQL ping for price update on change.
  const KEYS_TO_LISTEN_FOR_CHANGES: Array<keyof GetQuoteForm.Variables['quote']> = [
    'videoProjectTemplateSlug',
    'shootStartAt',
    'masterTimeProfileSlug',
    'masterServiceQualityLevelSlug',
    'jobTemplatePackages',
  ]

  setLocalPurchaseData({
    videoProjectTemplateType,
    quote: nextValues,
    trackingUuid: trackingUuid || null,
  })

  if (intersection(KEYS_TO_LISTEN_FOR_CHANGES, keysOfChangedValues).length === 0) {
    return
  }

  refetch({
    quote: finalizeQuoteData({ data: nextValues, path }),
    videoProjectTemplateType,
    trackingUuid: trackingUuid || null,
  })
}

const PURCHASE_CUSTOM_TITLE = 'PURCHASE_CUSTOM_TITLE'

interface FinalDataProps {
  data: QuoteInput
  path: string
}

export const finalizeQuoteData = ({ data, path }: FinalDataProps) => {
  const hasCustomTitle = myStorage.getItem(PURCHASE_CUSTOM_TITLE) || 'false'
  if (!JSON.parse(hasCustomTitle)) {
    data.title = null
  }
  return setMarketplaceId({ data, path })
}

if (window.analytics && window.analytics.page) {
  window.analytics.page()
}

export const SHOOT_ANYWHERE_PRODUCT_ID = 1144
export const EDIT_NOW_PRODUCT_ID = 1145
export const FULL_PROJECT_PRODUCT_ID = 1146

export const setMarketplaceId = ({ data, path }: FinalDataProps) => {
  const clonedData = JSON.stringify(data)
  const finalData = JSON.parse(clonedData)
  switch (path) {
    case '/create/shoot':
    default:
      finalData.marketPlaceProductId = SHOOT_ANYWHERE_PRODUCT_ID
      return finalData
    case '/create/project':
      finalData.marketPlaceProductId = FULL_PROJECT_PRODUCT_ID
      return finalData
    case '/create/edit':
      finalData.marketPlaceProductId = EDIT_NOW_PRODUCT_ID
      return finalData
  }
}

export const getTemplateType = (path: string) => {
  switch (path) {
    case '/create/shoot':
      return VideoProjectTemplateType.SHOOT
    case '/create/project':
      return VideoProjectTemplateType.ALL
    default:
      return VideoProjectTemplateType.SHOOT
  }
}
interface MatchProps {
  match?: { params: { id: string } }
}

const Purchase = (props: RouteComponentProps & MatchProps) => {
  React.useEffect(() => {
    localStorage.removeItem('currentBrandId')
  }, [])

  const brandId = props.match && props.match.params.id
  return (
    <>
      <Container>
        <Left>
          <SmallTitle>Submit a video brief</SmallTitle>
          <SmallDescription>
            Complete the following fields to get a more accurate quote. Our Service Team will reach
            out to you shortly with a customised quote.
          </SmallDescription>
        </Left>
        <Right>
          <Component<{}, State> initialState={{ screen: 'SHOW_FORM' }}>
            {({ setState, state }) => (
              <React.Fragment>
                {state.screen === 'SHOW_FORM' && (
                  <Query<GetQuoteForm.Query, GetQuoteForm.Variables>
                    notifyOnNetworkStatusChange
                    query={GET_QUOTE_FORM}
                    errorPolicy="all"
                    variables={getLocalPurchaseData(getTemplateType(props.location.pathname))}
                  >
                    {({ loading, error, data: queryData, refetch, networkStatus }) => {
                      const data = queryData

                      // This is unlikely to happen but could happen if we update the request schema in the backend
                      // and the frontend deployment is not active yet. Remember to provide a migration path for old
                      // localStorage data to new schema whenever there is a schema change or else this message will appear.
                      if (error) {
                        clearLocalState()
                        refetch(getLocalPurchaseData(getTemplateType(props.location.pathname)))
                      }

                      if (networkStatus === 1 || !data || (data && !data.getQuoteForm)) {
                        return <Loading />
                      }

                      return (
                        <PurchaseOrderRedesigned
                          isFetching={loading}
                          initialValues={mapQuoteToQuoteInput(data.getQuoteForm.quote)}
                          availableOptions={data.getQuoteForm}
                          onChange={handleFormChange(
                            refetch,
                            VideoProjectTemplateType.ALL,
                            data.getQuoteForm.trackingUuid,
                            '/create/project'
                          )}
                          onSubmit={quote => {
                            localStorage.setItem('currentBrandId', brandId)
                            setLocalPurchaseData({
                              videoProjectTemplateType: VideoProjectTemplateType.ALL,
                              quote,
                              trackingUuid: data.getQuoteForm.trackingUuid || null,
                            })
                            return handleFormSubmit(setState, VideoProjectTemplateType.ALL).call(
                              quote
                            )
                          }}
                        />
                      )
                    }}
                  </Query>
                )}
                {state.screen === 'HAS_AUTHENTICATED' && (
                  <Redirect
                    push
                    to={`/auth/handoff/brand-selection?videoProjectTemplateType=${
                      state.videoProjectTemplateType
                    }`}
                  />
                )}
              </React.Fragment>
            )}
          </Component>
        </Right>
      </Container>
    </>
  )
}

export default withRouter(Purchase)



// WEBPACK FOOTER //
// ./src/components/PurchaseRedesigned/PurchaseRedesigned.tsx